/**
 * This files contains non-react functions which support datasetpage.js
 *
 * See also: datasetpage_utils.test.js
 */


/**
 * Generate a Python code snippet for display in API Access.
 *
 * @param cdasId (ID of the dataset in CDAS API)
 * @param apiSnippetVariables (List of variable name strings to use in code)
 * @returns string of code
 */
export function getPythonCdasApiCodeSnippet(cdasId) {

    return `
from cdasws import CdasWs
cdas = CdasWs()

dataset = '${cdasId}'
var_names = cdas.get_variable_names(dataset)

print('Variable names:', var_names)

example_interval = cdas.get_example_time_interval(dataset)
print('Example time interval:', example_interval)

status, data = cdas.get_data(dataset, var_names, example_interval)

if 'spacepy' in str(type(data)):
    #  see https://spacepy.github.io/datamodel.html
    print(var_names[0], '=', data[var_names[0]])
    print(data[var_names[0]].attrs)
else:
    #  see https://github.com/MAVENSDC/cdflib
    print(var_names[0], '=', data.data_vars[var_names[0]].values)
    print(data.data_vars[var_names[0]].attrs)

print(data)
# ...
`;
}

/**
 * Generate an IDL code snippet for display in API Access.
 *
 * @param cdasId (ID of the dataset in CDAS API)
 * @return string of code
 */
export function getIdlCdasApiCodeSnippet(cdasId) {
    return `
compile_opt idl2
savFilename = filepath('spdfcdas.sav', /tmp)
oUrl = obj_new('IDLnetUrl')
;  For IDL installations with old root certificates
oUrl->setProperty, SSL_VERIFY_PEER=0
savFilename = oUrl->get(filename=savFilename, url='https://cdaweb.gsfc.nasa.gov/WebServices/REST/spdfcdas.sav')
restore, savFilename

cdas = obj_new('SpdfCdas')
dataset = '${cdasId}'
varNames = cdas.getVariableNames(dataset)
print, 'varNames:', varNames
exampleTime = cdas.getExampleTimeInterval(dataset)
exampleTime.print
; Edit varNames and exampleTime to suit your needs.
data = cdas.getCdawlibData([exampleTime], dataset, varNames[0])
print, varNames[0], ':', data.(0).dat
;  ...
;  For additional info. about each variable, type
;  help, /struct, data.variablename
;  For GUI access to all CDAWeb datasets, use
;  spdfcdawebchooser
end
`;
}


/**
 * This files contains non-react functions which support datasetpage.js
 *
 * See also: datasetpage_utils.test.js
 */


/**
 * Generate a Python code snippet for display in API Access.
 *
 * @param startTime string that indicates start time of observation
 * @param endTime string that indicates end time of observation
 * @param wavelength Object that hold attributes of wavelengths observed
 * @param instrument string that identifies the instrument utilized
 * @returns string of code
 */
export function getPythonSunpyApiCodeSnippet(startTime, wavelength, instrument) {
    let startObj = new Date(startTime);
    let endObj = new Date(startObj.getTime() + 86400 * 1000);
    let strFormatted = startObj.toISOString().slice(0, 16);
    let endFormatted = endObj.toISOString().slice(0, 16);
    let wavelengthComment, wavelengthText, highWavelengthText, waveUnitsPad, wavelengthBodyText;

    if(wavelength) {
        waveUnitsPad = wavelength?.units === "nanometers" || wavelength.units === "nm" ? "0" : "";
        wavelengthComment = `\n# Available wavelengths for ${instrument}: ${wavelength.low} ${wavelength.units}`;
        wavelengthComment = wavelength.high ? wavelengthComment + ` to ${wavelength.high} ${wavelength.units}` : wavelengthComment;

        wavelengthText = "low_wavelength";
        wavelengthText = wavelengthText + ` = ${wavelength.low}${waveUnitsPad} * u.angstrom`

        highWavelengthText = 'high_wavelength = ' + wavelength?.high;
        highWavelengthText = highWavelengthText + waveUnitsPad  + ' * u.angstrom\n';

        wavelengthBodyText = `${wavelengthComment}\n`;
        wavelengthBodyText = wavelengthBodyText + `${wavelengthText}\n`;
        wavelengthBodyText = wavelength?.high ?  wavelengthBodyText + `${highWavelengthText}` : wavelengthBodyText;
    }

    return `from sunpy.net import Fido, attrs as a
import sunpy.map
from astropy import units as u

start_time = "${strFormatted}"
end_time = "${endFormatted}"
${wavelengthBodyText || ""}
# Query list of files
result = Fido.search(
        a.Time(start_time, end_time),
        a.Instrument."Add instrument"${wavelength ? "," : ")"}
        ${wavelength ? `a.Wavelength(low_wavelength${wavelength?.high ? ", high_wavelength)":""})\n` : ""}
print(result)

# Download first file
downloaded_files = Fido.fetch(result[0, 0])

# Display the map in a browser window. An array of data values is available
# in the attribute 'map.data'
map = sunpy.map.Map(downloaded_files[0])
map.quicklook()
`;
}
